import { BALANCE } from './balance';
import type { PlayerId, PlayerState, Vec3 } from './types';

/**
 * SECURITY — cameras, contacts, and the guard's map.
 *
 * A camera is a cone. A thief inside the cone for long enough becomes a
 * CONTACT, which is what the guard actually sees: a marker that persists for a
 * few seconds after the thief breaks line of sight. Guards chase a memory, not
 * a live dot, which is what makes breaking sight feel like an achievement.
 *
 * Line of sight is the caller's job — the sim has no geometry. The host passes
 * a `visible` predicate backed by a physics raycast; tests pass a stub.
 */

export interface CameraDef {
  id: string;
  pos: Vec3;
  /** Unit vector the camera looks along. */
  dir: Vec3;
  /** Which bank this camera belongs to, for the control-room wall and jammers. */
  bank: string;
  fovDeg?: number;
  rangeM?: number;
}

export interface CameraRuntime {
  def: CameraDef;
  /** Seconds this camera has had continuous sight of someone. */
  dwell: number;
  /** Zero while healthy; counts down while jammed. */
  jammedFor: number;
  /** Who it is currently looking at, if anyone. */
  looking: PlayerId | null;
}

export interface Contact {
  thief: PlayerId;
  at: Vec3;
  /** Seconds until this contact fades off the guard's map. */
  ttl: number;
  /** Which camera or noise event produced it. */
  source: string;
}

export type VisibilityFn = (from: Vec3, to: Vec3) => boolean;

export class SecuritySystem {
  readonly cameras: CameraRuntime[] = [];
  /** Live contacts, newest first, one per thief. */
  contacts = new Map<PlayerId, Contact>();

  constructor(defs: CameraDef[] = []) {
    for (const def of defs) this.add(def);
  }

  add(def: CameraDef): CameraRuntime {
    const cam: CameraRuntime = { def, dwell: 0, jammedFor: 0, looking: null };
    this.cameras.push(cam);
    return cam;
  }

  /** Knock out every camera within `radius` of a point for `seconds`. */
  jam(at: Vec3, radius: number, seconds: number): string[] {
    const hit: string[] = [];
    for (const cam of this.cameras) {
      if (dist(cam.def.pos, at) <= radius) {
        cam.jammedFor = Math.max(cam.jammedFor, seconds);
        cam.dwell = 0;
        cam.looking = null;
        hit.push(cam.def.id);
      }
    }
    return hit;
  }

  /** A noise event drops a contact at its origin — imprecise, but real. */
  reportNoise(thief: PlayerId, at: Vec3, source = 'noise'): void {
    this.contacts.set(thief, {
      thief,
      at: { ...at },
      ttl: BALANCE.security.contactMemorySec,
      source,
    });
  }

  tick(dt: number, thieves: PlayerState[], visible: VisibilityFn): void {
    // Age existing contacts BEFORE looking, so a contact confirmed this tick
    // gets its full memory window rather than being docked one frame of it.
    for (const [id, c] of this.contacts) {
      c.ttl -= dt;
      if (c.ttl <= 0) this.contacts.delete(id);
    }

    for (const cam of this.cameras) {
      if (cam.jammedFor > 0) {
        cam.jammedFor -= dt;
        continue;
      }

      const seen = thieves.find(
        (t) => t.status === 'active' && inCone(cam.def, t.pos) && visible(cam.def.pos, eyeOf(t)),
      );

      if (!seen) {
        cam.dwell = 0;
        cam.looking = null;
        continue;
      }

      // A different thief walking into frame restarts the clock. Cameras confirm
      // a person, not a blur.
      if (cam.looking !== seen.id) {
        cam.looking = seen.id;
        cam.dwell = 0;
      }
      cam.dwell += dt;
      if (cam.dwell >= BALANCE.security.cameraDwellSec) {
        this.contacts.set(seen.id, {
          thief: seen.id,
          at: { ...seen.pos },
          ttl: BALANCE.security.contactMemorySec,
          source: cam.def.id,
        });
      }
    }
  }

  /** What the control room wall shows right now. */
  activeContacts(): Contact[] {
    return [...this.contacts.values()].sort((a, b) => b.ttl - a.ttl);
  }

  isJammed(cameraId: string): boolean {
    const cam = this.cameras.find((c) => c.def.id === cameraId);
    return !!cam && cam.jammedFor > 0;
  }
}

/**
 * Guard chute — the Among Us style fast-travel that makes one guard able to
 * cover a mall. Long cooldown so it is a commitment, not a teleport spam.
 */
export class ChuteNetwork {
  private cooldown = new Map<PlayerId, number>();

  constructor(readonly exits: Array<{ id: string; pos: Vec3 }>) {}

  tick(dt: number): void {
    for (const [id, t] of this.cooldown) {
      const next = t - dt;
      if (next <= 0) this.cooldown.delete(id);
      else this.cooldown.set(id, next);
    }
  }

  remaining(guard: PlayerId): number {
    return this.cooldown.get(guard) ?? 0;
  }

  /** Returns the destination, or null if the chute is on cooldown. */
  travel(guard: PlayerId, exitId: string): Vec3 | null {
    if (this.remaining(guard) > 0) return null;
    const exit = this.exits.find((e) => e.id === exitId);
    if (!exit) return null;
    this.cooldown.set(guard, BALANCE.security.chuteCooldownSec);
    return { ...exit.pos };
  }
}

function inCone(cam: CameraDef, target: Vec3): boolean {
  const range = cam.rangeM ?? BALANCE.security.cameraRangeM;
  const half = ((cam.fovDeg ?? BALANCE.security.cameraFovDeg) * Math.PI) / 180;
  const dx = target.x - cam.pos.x;
  const dy = target.y - cam.pos.y;
  const dz = target.z - cam.pos.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len > range || len === 0) return false;
  const dot = (dx * cam.dir.x + dy * cam.dir.y + dz * cam.dir.z) / len;
  return dot >= Math.cos(half);
}

/** Cameras look for heads, not feet. */
function eyeOf(p: PlayerState): Vec3 {
  return { x: p.pos.x, y: p.pos.y + BALANCE.move.standHeight * 0.9, z: p.pos.z };
}

function dist(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
